/**
 * ReviewAndPay — Settlement Payment Portal
 * Route: /review?token=<SETTLEMENT_TOKEN>
 *
 * Public page (no auth required).
 * Decodes the encrypted settlement token from Angela's send_payment_link call,
 * displays the agreement summary, signing status, and launches Paywire OSBP.
 *
 * Flow:
 *   1. Decode token via POST /api/settlement/token/validate
 *   2. Show agreement summary (name, amount, schedule, expiry)
 *   3. Show Documenso signing status (optional — if document_id present)
 *   4. "Pay Now" → POST /api/paywire/osbp/init → redirect to Paywire OSBP
 */
import { useState, useEffect, useRef } from "react";
import { useSearch } from "wouter";
import {
  Shield,
  Phone,
  Lock,
  AlertCircle,
  Loader2,
  CheckCircle2,
  FileText,
  Calendar,
  DollarSign,
  ChevronRight,
  ExternalLink,
  Clock,
  CreditCard,
} from "lucide-react";
import { Button } from "@/components/ui/button";

// Brand colors
const BRAND = {
  bg: "#0a0f1a",
  card: "#111827",
  cardHover: "#161f30",
  border: "#1f2937",
  accent: "#3b82f6",
  accentLight: "#60a5fa",
  gold: "#f59e0b",
  green: "#10b981",
  red: "#ef4444",
  text: "#f9fafb",
  textMuted: "#9ca3af",
  textDim: "#6b7280",
};

interface TokenPayload {
  account_id: string;
  settlement_amount: number;
  debtor_name: string;
  debtor_phone: string;
  document_id: string;
  down_payment: number;
  payment_schedule: Array<{ date: string; amount: number }>;
  expiration_date: string;
  expires_at: string;
}

type Step = "loading" | "review" | "signing" | "paying" | "redirecting" | "error";

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateStr: string): string {
  if (!dateStr) return "";
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return dateStr;
  }
}

export default function ReviewAndPay() {
  const searchString = useSearch();
  const params = new URLSearchParams(searchString);
  const token = params.get("token");

  const [step, setStep] = useState<Step>("loading");
  const [payload, setPayload] = useState<TokenPayload | null>(null);
  const [error, setError] = useState<string>("");
  const [signingStatus, setSigningStatus] = useState<"unknown" | "pending" | "signed">("unknown");
  const [osbpData, setOsbpData] = useState<{ osbpUrl: string; paymentRequest: string } | null>(null);
  const formRef = useRef<HTMLFormElement>(null);

  // Decode token on mount
  useEffect(() => {
    if (!token) {
      setError("Invalid payment link. Please use the link provided in your text message.");
      setStep("error");
      return;
    }

    async function validateToken() {
      try {
        const res = await fetch("/api/settlement/token/validate", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ token }),
        });

        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.detail || data.error || "This payment link is invalid or has expired.");
        }

        const data = await res.json();
        setPayload(data);
        setStep("review");

        // Check signing status if document_id present
        if (data.document_id) {
          checkSigningStatus(data.account_id, data.document_id);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load your payment information.");
        setStep("error");
      }
    }

    validateToken();
  }, [token]);

  // Auto-submit OSBP form when data is ready
  useEffect(() => {
    if (osbpData && formRef.current) {
      formRef.current.submit();
    }
  }, [osbpData]);

  async function checkSigningStatus(accountId: string, documentId: string) {
    try {
      const res = await fetch(
        `/api/settlement/document-status?account_id=${encodeURIComponent(accountId)}&document_id=${encodeURIComponent(documentId)}`
      );
      if (res.ok) {
        const data = await res.json();
        setSigningStatus(data.signed ? "signed" : "pending");
      }
    } catch {
      // Non-critical — don't block payment
      setSigningStatus("unknown");
    }
  }

  async function handlePayNow() {
    if (!payload) return;
    setStep("paying");

    try {
      const nameParts = payload.debtor_name.trim().split(/\s+/);
      const firstName = nameParts[0] || "Customer";
      const lastName = nameParts.slice(1).join(" ") || "Customer";

      // Determine first payment amount
      const firstPaymentAmount =
        payload.down_payment > 0
          ? payload.down_payment
          : payload.payment_schedule.length > 0
          ? payload.payment_schedule[0].amount
          : payload.settlement_amount;

      const res = await fetch("/api/paywire/osbp/init", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          amount: firstPaymentAmount,
          accountId: payload.account_id,
          firstName,
          lastName,
          phone: payload.debtor_phone || "",
          description:
            payload.down_payment > 0
              ? `Settlement Down Payment — ${formatCurrency(payload.settlement_amount)} total`
              : payload.payment_schedule.length > 1
              ? `Settlement Payment 1 of ${payload.payment_schedule.length}`
              : `Settlement — ${formatCurrency(payload.settlement_amount)}`,
          type: "sale",
          requestToken: payload.payment_schedule.length > 1,
          settlementToken: token,
          planDetails:
            payload.payment_schedule.length > 1
              ? {
                  totalAmount: payload.settlement_amount,
                  schedule: payload.payment_schedule,
                  downPayment: payload.down_payment,
                }
              : undefined,
        }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || "Failed to initialize payment. Please try again.");
      }

      const data = await res.json();
      setOsbpData({ osbpUrl: data.osbpUrl, paymentRequest: data.paymentRequest });
      setStep("redirecting");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Payment initialization failed.");
      setStep("error");
    }
  }

  // Hidden OSBP form
  const OsbpForm = () => {
    if (!osbpData) return null;
    return (
      <form ref={formRef} method="POST" action={osbpData.osbpUrl} style={{ display: "none" }}>
        <input type="hidden" name="PWREQUEST" value={osbpData.paymentRequest} />
      </form>
    );
  };

  return (
    <div className="min-h-screen" style={{ background: BRAND.bg }}>
      <OsbpForm />

      {/* Header */}
      <header className="border-b sticky top-0 z-10" style={{ borderColor: BRAND.border, background: BRAND.bg }}>
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <img src="/favicon-192x192.png" alt="Unifi" className="w-8 h-8 rounded-lg" />
            <div>
              <span className="text-base font-semibold text-white">Heights Finance</span>
              <p className="text-xs" style={{ color: BRAND.textMuted }}>Settlement Portal</p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 text-xs" style={{ color: BRAND.textMuted }}>
            <Lock className="w-3.5 h-3.5" />
            <span>Secure & Encrypted</span>
          </div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-8 pb-24">
        {/* ── Error ── */}
        {step === "error" && (
          <div className="text-center py-16">
            <div
              className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ background: "rgba(239,68,68,0.1)" }}
            >
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h2 className="text-xl font-bold text-white mb-2">Link Unavailable</h2>
            <p className="mb-6 max-w-sm mx-auto" style={{ color: BRAND.textMuted }}>
              {error}
            </p>
            <p className="text-sm" style={{ color: BRAND.textMuted }}>
              Need help?{" "}
              <a href="tel:+17027613617" className="text-blue-400 hover:underline">
                (702) 761-3617
              </a>
            </p>
          </div>
        )}

        {/* ── Loading ── */}
        {step === "loading" && (
          <div className="text-center py-16">
            <Loader2
              className="w-10 h-10 animate-spin mx-auto mb-4"
              style={{ color: BRAND.accent }}
            />
            <p style={{ color: BRAND.textMuted }}>Loading your settlement details...</p>
          </div>
        )}

        {/* ── Redirecting ── */}
        {step === "redirecting" && (
          <div className="text-center py-16">
            <Loader2
              className="w-10 h-10 animate-spin mx-auto mb-4"
              style={{ color: BRAND.green }}
            />
            <p className="text-white font-medium mb-1">Redirecting to secure payment...</p>
            <p className="text-sm" style={{ color: BRAND.textMuted }}>
              You'll be taken to our encrypted payment processor.
            </p>
          </div>
        )}

        {/* ── Review ── */}
        {(step === "review" || step === "paying") && payload && (
          <div>
            {/* Greeting */}
            <div className="mb-6">
              <h1 className="text-2xl font-bold text-white mb-1">
                Hi, {payload.debtor_name.split(" ")[0]}
              </h1>
              <p style={{ color: BRAND.textMuted }}>
                Review your settlement agreement and complete your payment below.
              </p>
            </div>

            {/* Settlement Summary Card */}
            <div
              className="rounded-xl p-5 mb-4"
              style={{ background: BRAND.card, border: `1px solid ${BRAND.border}` }}
            >
              <div className="flex items-center gap-2 mb-4">
                <DollarSign className="w-4 h-4" style={{ color: BRAND.gold }} />
                <h2 className="font-semibold text-white">Settlement Summary</h2>
              </div>

              <div className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-sm" style={{ color: BRAND.textMuted }}>
                    Account Holder
                  </span>
                  <span className="text-sm font-medium text-white">{payload.debtor_name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-sm" style={{ color: BRAND.textMuted }}>
                    Settlement Amount
                  </span>
                  <span className="text-lg font-bold" style={{ color: BRAND.green }}>
                    {formatCurrency(payload.settlement_amount)}
                  </span>
                </div>
                {payload.down_payment > 0 && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{ color: BRAND.textMuted }}>
                      Due Today (Down Payment)
                    </span>
                    <span className="text-sm font-semibold" style={{ color: BRAND.gold }}>
                      {formatCurrency(payload.down_payment)}
                    </span>
                  </div>
                )}
                {payload.expiration_date && (
                  <div className="flex justify-between items-center">
                    <span className="text-sm" style={{ color: BRAND.textMuted }}>
                      Offer Expires
                    </span>
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5" style={{ color: BRAND.gold }} />
                      <span className="text-sm font-medium" style={{ color: BRAND.gold }}>
                        {payload.expiration_date}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Schedule (if installments) */}
            {payload.payment_schedule && payload.payment_schedule.length > 0 && (
              <div
                className="rounded-xl p-5 mb-4"
                style={{ background: BRAND.card, border: `1px solid ${BRAND.border}` }}
              >
                <div className="flex items-center gap-2 mb-4">
                  <Calendar className="w-4 h-4" style={{ color: BRAND.accent }} />
                  <h2 className="font-semibold text-white">Payment Schedule</h2>
                  <span
                    className="ml-auto text-xs px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(59,130,246,0.1)", color: BRAND.accentLight }}
                  >
                    {payload.payment_schedule.length} payments
                  </span>
                </div>
                <div className="space-y-2">
                  {payload.payment_schedule.map((pmt, i) => (
                    <div
                      key={i}
                      className="flex justify-between items-center py-2 border-b last:border-0"
                      style={{ borderColor: BRAND.border }}
                    >
                      <div className="flex items-center gap-2">
                        <div
                          className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold"
                          style={{
                            background:
                              i === 0 ? "rgba(245,158,11,0.15)" : "rgba(59,130,246,0.1)",
                            color: i === 0 ? BRAND.gold : BRAND.accentLight,
                          }}
                        >
                          {i + 1}
                        </div>
                        <span className="text-sm" style={{ color: i === 0 ? BRAND.text : BRAND.textMuted }}>
                          {pmt.date}
                        </span>
                        {i === 0 && (
                          <span
                            className="text-xs px-1.5 py-0.5 rounded"
                            style={{ background: "rgba(245,158,11,0.1)", color: BRAND.gold }}
                          >
                            Due Today
                          </span>
                        )}
                      </div>
                      <span
                        className="text-sm font-semibold"
                        style={{ color: i === 0 ? BRAND.gold : BRAND.text }}
                      >
                        {formatCurrency(pmt.amount)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* E-Sign Status */}
            {payload.document_id && (
              <div
                className="rounded-xl p-5 mb-4"
                style={{ background: BRAND.card, border: `1px solid ${BRAND.border}` }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-4 h-4" style={{ color: BRAND.accent }} />
                  <h2 className="font-semibold text-white">Settlement Agreement</h2>
                </div>

                {signingStatus === "signed" ? (
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-5 h-5" style={{ color: BRAND.green }} />
                    <span className="text-sm" style={{ color: BRAND.green }}>
                      Agreement signed — you're ready to pay
                    </span>
                  </div>
                ) : signingStatus === "pending" ? (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <Clock className="w-4 h-4" style={{ color: BRAND.gold }} />
                      <span className="text-sm" style={{ color: BRAND.gold }}>
                        Agreement not yet signed
                      </span>
                    </div>
                    <p className="text-xs mb-3" style={{ color: BRAND.textMuted }}>
                      Please sign your agreement using the link sent to your phone before completing payment.
                    </p>
                    <a
                      href={`https://sign.unifipro.ai`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                    >
                      Open signing link <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                ) : (
                  <p className="text-sm" style={{ color: BRAND.textMuted }}>
                    Your settlement agreement was sent to your phone. Please sign it before completing payment.
                  </p>
                )}
              </div>
            )}

            {/* Security Badges */}
            <div className="flex items-center justify-center gap-6 mb-6">
              <div className="flex items-center gap-1.5 text-xs" style={{ color: BRAND.textDim }}>
                <Shield className="w-3.5 h-3.5" />
                <span>256-bit SSL</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs" style={{ color: BRAND.textDim }}>
                <CreditCard className="w-3.5 h-3.5" />
                <span>PCI Compliant</span>
              </div>
              <div className="flex items-center gap-1.5 text-xs" style={{ color: BRAND.textDim }}>
                <Lock className="w-3.5 h-3.5" />
                <span>Encrypted</span>
              </div>
            </div>

            {/* Pay Now Button */}
            <Button
              onClick={handlePayNow}
              disabled={step === "paying"}
              className="w-full h-14 text-base font-semibold rounded-xl"
              style={{
                background:
                  step === "paying"
                    ? "rgba(16,185,129,0.5)"
                    : "linear-gradient(135deg, #10b981, #059669)",
                color: "white",
                border: "none",
              }}
            >
              {step === "paying" ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Preparing secure payment...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  {payload.down_payment > 0
                    ? `Pay ${formatCurrency(payload.down_payment)} Now`
                    : payload.payment_schedule.length > 0
                    ? `Pay ${formatCurrency(payload.payment_schedule[0]?.amount ?? payload.settlement_amount)} Now`
                    : `Pay ${formatCurrency(payload.settlement_amount)} Now`}
                  <ChevronRight className="w-4 h-4" />
                </span>
              )}
            </Button>

            <p className="text-center text-xs mt-3" style={{ color: BRAND.textDim }}>
              You'll be redirected to our secure payment processor (Paywire).
              No card information is stored on this site.
            </p>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer
        className="fixed bottom-0 left-0 right-0 border-t py-3"
        style={{ borderColor: BRAND.border, background: BRAND.bg }}
      >
        <div className="max-w-2xl mx-auto px-4 flex items-center justify-between">
          <p className="text-xs" style={{ color: BRAND.textDim }}>
            Heights Finance Corp. | (702) 761-3617
          </p>
          <a
            href="tel:+17027613617"
            className="flex items-center gap-1.5 text-xs"
            style={{ color: BRAND.textMuted }}
          >
            <Phone className="w-3.5 h-3.5" />
            Need help?
          </a>
        </div>
      </footer>
    </div>
  );
}
